import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/authUtils';
import { PERMISSIONS, useCan } from '../authority/permissions';
import contentManagementImage from '../../assets/images/content_management.webp';

const PageContainer = styled.div`
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
`;

const MainContent = styled.main`
  width: 100%;
  max-width: 800px;
  padding: 2rem;
`;

const DashboardTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 2rem;
  text-align: center;
`;

const LinkGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
`;

const StyledLink = styled(Link)`
  width: 200px;
  height: 200px;
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LinkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const LinkText = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  text-align: center;
`;

// The Program and People tiles have no photo of their own; the same tile shape with the brand navy
// instead. They replace the User Management and Speaker Management tiles at the switch (plan §7.7
// item 1); those two screens stay in the tree, unlinked, until the cleanup release.
const PlainLink = styled(StyledLink)`
  background-image: none;
  background-color: #111D4E;

  ${LinkText} {
    color: #fff;
  }
`;

const LinkCount = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  color: #fff;
  text-align: center;
`;

const AdminDashboard = () => {
  const can = useCan();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Open abstract submissions; stays null (tile without a number) while unknown or when the call fails.
  const [openSubmissions, setOpenSubmissions] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      await api.get('/api/protected');
      setIsLoggedIn(true);
      try {
        const { data } = await api.get('/api/cms/submissions/open-count');
        if (typeof data?.count === 'number') setOpenSubmissions(data.count);
      } catch (countError) {
        setOpenSubmissions(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsLoggedIn(false);
      navigate('/admin/login');
    }
  };

  if (!isLoggedIn) {
    return (
      <PageContainer>
        <MainContent>
          <Link to="/" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            Return to Home
          </Link>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MainContent>
        <DashboardTitle>Admin Dashboard</DashboardTitle>
        <LinkGrid>
          <StyledLink to="/admin/content-management" bgImage={contentManagementImage}>
            <LinkOverlay>
              <LinkText>Content Management</LinkText>
            </LinkOverlay>
          </StyledLink>
          <PlainLink to="/admin/program">
            <LinkOverlay>
              <LinkText>Program</LinkText>
            </LinkOverlay>
          </PlainLink>
          <PlainLink to="/admin/people">
            <LinkOverlay>
              <LinkText>People</LinkText>
            </LinkOverlay>
          </PlainLink>
          {/* The sent log; the API is the staff area's, so admins and the team both see it. */}
          <PlainLink to="/admin/emails">
            <LinkOverlay>
              <LinkText>E-Mails</LinkText>
            </LinkOverlay>
          </PlainLink>
          {can(PERMISSIONS.staffManage) && (
            <PlainLink to="/admin/team">
              <LinkOverlay>
                <LinkText>Team</LinkText>
              </LinkOverlay>
            </PlainLink>
          )}
          <PlainLink to="/admin/submission-management">
            <LinkOverlay>
              <div>
                <LinkText>Abstract submissions</LinkText>
                {openSubmissions !== null && <LinkCount>{openSubmissions} open</LinkCount>}
              </div>
            </LinkOverlay>
          </PlainLink>
        </LinkGrid>
      </MainContent>
    </PageContainer>
  );
};

export default AdminDashboard;